import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AxiosError } from 'axios';

import { checkIpAllowList, getClientIpAddresses } from 'services/api';
import { useNetworkCheck } from 'contexts/NetworkContextProvider';

type IpAllowListContextType = {
  isCheckingIpAccess: boolean;
  isIpAccessAllowed: boolean;
};

type IpAllowListProviderProps = {
  children: ReactNode;
};

const IpAllowListContext = createContext<IpAllowListContextType | undefined>(undefined);

const extractAllowedFlag = (payload: unknown): boolean => {
  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload);
      return extractAllowedFlag(parsed);
    } catch (_error) {
      return false;
    }
  }

  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const maybeAllowed = (payload as any).allowed ?? (payload as any).data?.allowed;

  return maybeAllowed === true || maybeAllowed === 'true' || maybeAllowed === 1 || maybeAllowed === '1';
};

export const IpAllowListProvider: React.FC<IpAllowListProviderProps> = ({ children }) => {
  const [isCheckingIpAccess, setIsCheckingIpAccess] = useState(true);
  const [isIpAccessAllowed, setIsIpAccessAllowed] = useState(false);
  const { isOnline } = useNetworkCheck();
  const latestCheckRequestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const validateIpAccess = useCallback(async () => {
    const requestId = ++latestCheckRequestIdRef.current;
    const shouldApplyResult = () => isMountedRef.current && requestId === latestCheckRequestIdRef.current;

    if (!isOnline) {
      if (shouldApplyResult()) {
        setIsCheckingIpAccess(false);
        setIsIpAccessAllowed(false);
      }
      return;
    }

    try {
      if (shouldApplyResult()) {
        setIsCheckingIpAccess(true);
      }
      const ipAddresses = await getClientIpAddresses();
      const checkResults = await Promise.allSettled(ipAddresses.map(ipAddress => checkIpAllowList(ipAddress)));

      const isAnyAddressAllowed = checkResults.some(result => {
        if (result.status !== 'fulfilled') {
          return false;
        }

        return extractAllowedFlag(result.value.data);
      });

      if (!isAnyAddressAllowed) {
        const failedStatuses = checkResults
          .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
          .map(result => {
            const error = result.reason as AxiosError;
            return error.response?.status ?? 'network_error';
          });

        const fulfilledPayloads = checkResults
          .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
          .map(result => result.value.data);

        console.warn('IP allow-list denied access.', {
          ipAddresses,
          failedStatuses,
          fulfilledPayloads,
        });
      }

      if (shouldApplyResult()) {
        setIsIpAccessAllowed(isAnyAddressAllowed);
      }
    } catch (error) {
      if (shouldApplyResult()) {
        console.error('Failed to verify IP allow-list access.', error);
        setIsIpAccessAllowed(false);
      }
    } finally {
      if (shouldApplyResult()) {
        setIsCheckingIpAccess(false);
      }
    }
  }, [isOnline]);

  useEffect(() => {
    validateIpAccess();
  }, [validateIpAccess]);

  const contextValue = useMemo(
    () => ({
      isCheckingIpAccess,
      isIpAccessAllowed,
    }),
    [isCheckingIpAccess, isIpAccessAllowed],
  );

  return <IpAllowListContext.Provider value={contextValue}>{children}</IpAllowListContext.Provider>;
};

export const useIpAllowList = () => {
  const context = useContext(IpAllowListContext);

  if (!context) {
    throw new Error('useIpAllowList must be used within IpAllowListProvider');
  }

  return context;
};
